import { CampaignMedium } from '@prisma/client';
import { Grid2, Stack } from '@mui/material';
import NewTemplateCard from '../CampaignTemplateDesigner/NewTemplateCard';
import TemplateCard from '../CampaignTemplateDesigner/TemplateCard';
import { TemplateEmbedData } from '../CampaignTemplateDesigner/TemplateEmbed';
import {
  MarketingEmailTemplateWithSource,
  MarketingSmsTemplateWithSource,
  PocketflowsProjectEmailTemplate,
} from '../../../types/marketing';
import {
  getEmailTemplatePreviewUrl,
  getProjectEmailTemplatePreviewUrl,
  getSmsTemplatePreviewUrl,
} from '../../../helpers/marketing';
import useCreateTemplateEmbed from '../../../hooks/marketing/useCreateTemplateEmbed';
import useCopyEmailProjectTemplate from '../../../hooks/marketing/useCopyEmailProjectTemplate';

type Props = {
  medium: CampaignMedium;
  onClose: () => void;
  onEmbedTemplate: (result: TemplateEmbedData) => void | Promise<void>;
  onResetTabs: () => void;
  isFetching?: boolean;
} & (
  | {
      type: 'custom';
      onSelectTemplate: (
        template:
          | MarketingEmailTemplateWithSource
          | MarketingSmsTemplateWithSource
          | null,
      ) => void;
      templates?: (
        | MarketingSmsTemplateWithSource
        | MarketingEmailTemplateWithSource
      )[];
      selectedTemplateId?: number;
    }
  | {
      type: 'project';
      templates: PocketflowsProjectEmailTemplate[];
      selectedTemplateId?: string;
    }
);

function CampaignTemplatesList({
  medium,
  isFetching,
  onClose,
  onEmbedTemplate,
  onResetTabs,
  ...props
}: Props) {
  const {
    mutate: createEmbed,
    isPending: isPendingCreate,
    variables: createVariables,
  } = useCreateTemplateEmbed({
    onSuccess:
      props.type === 'custom'
        ? (result, resultTemplateId) => {
            onEmbedTemplate?.({
              id: resultTemplateId,
              url: result.url,
            });
          }
        : undefined,
  });
  const {
    mutateAsync: copyProjectTemplate,
    isPending: isPendingCopy,
    variables: copyVariables,
  } = useCopyEmailProjectTemplate({
    onSuccess:
      props.type === 'project'
        ? result => {
            onEmbedTemplate?.({
              id: result.template.id,
              url: result.embed.url,
            });
            onResetTabs();
          }
        : undefined,
  });

  return (
    <Stack gap={3} boxSizing="border-box" mt={2}>
      <Grid2
        container
        mt={2}
        columns={{
          xs: 1,
          sm: 2,
          md: 3,
          lg: 4,
        }}
        spacing={2.5}
        boxSizing="border-box">
        {props.type === 'custom' && (
          <>
            <NewTemplateCard
              medium={medium}
              onSuccess={result =>
                onEmbedTemplate!({
                  id: result.template.id,
                  url: result.embed.url,
                })
              }
            />
            {props.templates?.map(template => {
              const previewUrl =
                medium === 'EMAIL'
                  ? getEmailTemplatePreviewUrl(template)
                  : medium === 'SMS'
                  ? getSmsTemplatePreviewUrl(template)
                  : null;
              const isLoading =
                isPendingCreate && createVariables?.templateId === template.id;
              return (
                <TemplateCard
                  key={template.id}
                  id={template.id}
                  isSelected={
                    !!props.selectedTemplateId &&
                    props.selectedTemplateId === template.id
                  }
                  name={template?.source?.name}
                  previewUrl={previewUrl}
                  buttons={[
                    {
                      text: 'Apply',
                      onClick: () => {
                        props.onSelectTemplate(template);
                        onClose();
                      },
                      variant: 'base',
                      color: 'primary',
                    },
                    {
                      text: 'Preview',
                      onClick: () => {
                        window.open(previewUrl!, '_blank');
                      },
                      variant: 'light',
                      color: 'info',
                      hidden: !previewUrl,
                    },
                    {
                      text: 'Edit',
                      onClick: () =>
                        createEmbed({
                          templateId: template.id,
                          medium,
                        }),
                      variant: 'light',
                      color: 'info',
                      isLoading,
                    },
                  ]}
                  imageStyle={{
                    paddingInline:
                      previewUrl && medium === 'EMAIL' ? 0 : undefined,
                  }}
                  isPending={isLoading}
                />
              );
            })}
          </>
        )}
        {props.type === 'project' &&
          props.templates?.map(template => {
            const previewUrl =
              medium === 'EMAIL'
                ? getProjectEmailTemplatePreviewUrl(template)
                : null;
            const isLoading =
              isPendingCopy &&
              copyVariables?.sourceProjectTemplateId === template.id;
            return (
              <TemplateCard
                key={template.id}
                id={template.id}
                name={template.name}
                previewUrl={previewUrl}
                buttons={[
                  {
                    text: 'Edit',
                    onClick: () => {
                      copyProjectTemplate({
                        sourceProjectTemplateId: template.id,
                      });
                    },
                    variant: 'base',
                    color: 'primary',
                    isLoading,
                  },
                ]}
                imageStyle={{
                  paddingInline:
                    previewUrl && medium === 'EMAIL' ? 0 : undefined,
                }}
                isPending={isLoading}
              />
            );
          })}
      </Grid2>
    </Stack>
  );
}

export default CampaignTemplatesList;
